import { useTranslation } from 'react-i18next';
import { useLang } from '@/hooks/useLang';

const articles = [
  {
    slug: 'shoes-spreadsheet',
    title: 'AllChinaBuy Shoes Spreadsheet',
    desc: 'Complete guide to sneakers, boots, and footwear resources with sizing tips and QC strategies.',
    image: 'https://readdy.ai/api/search-image?query=Premium%20sneaker%20collection%20arranged%20on%20concrete%20shelf%20with%20warm%20spotlight%20lighting%20product%20photography%20clean%20aesthetic%20no%20text%20no%20brands&width=600&height=400&seq=art-card-shoes-01&orientation=landscape',
    icon: 'ri-shopping-bag-line',
  },
  {
    slug: 'clothing-spreadsheet',
    title: 'AllChinaBuy Clothing Spreadsheet',
    desc: 'Explore apparel categories, sizing systems, fabric research, and buying guides for clothing.',
    image: 'https://readdy.ai/api/search-image?query=Fashion%20clothing%20rack%20with%20garments%20in%20boutique%20with%20warm%20natural%20light%20editorial%20photography%20no%20text%20no%20brands&width=600&height=400&seq=art-card-clothing-01&orientation=landscape',
    icon: 'ri-shopping-bag-3-line',
  },
  {
    slug: 'bags-spreadsheet',
    title: 'AllChinaBuy Bags Spreadsheet',
    desc: 'Handbags, backpacks, and luggage resources with material guides and shipping tips.',
    image: 'https://readdy.ai/api/search-image?query=Luxury%20leather%20handbags%20on%20marble%20pedestals%20with%20soft%20spotlight%20lighting%20product%20photography%20no%20text%20no%20brands&width=600&height=400&seq=art-card-bags-01&orientation=landscape',
    icon: 'ri-shopping-bag-line',
  },
  {
    slug: 'accessories-spreadsheet',
    title: 'AllChinaBuy Accessories Spreadsheet',
    desc: 'Watches, jewelry, sunglasses, and fashion accessories with detailed QC insights.',
    image: 'https://readdy.ai/api/search-image?query=Fashion%20accessories%20watches%20sunglasses%20on%20velvet%20tray%20with%20warm%20lighting%20product%20photography%20no%20text%20no%20brands&width=600&height=400&seq=art-card-accessories-01&orientation=landscape',
    icon: 'ri-glasses-line',
  },
  {
    slug: 'electronics-spreadsheet',
    title: 'AllChinaBuy Electronics Spreadsheet',
    desc: 'Tech gadgets, headphones, and smart devices with safety and compatibility guides.',
    image: 'https://readdy.ai/api/search-image?query=Tech%20gadgets%20headphones%20on%20clean%20white%20desk%20with%20warm%20morning%20light%20technology%20photography%20no%20text%20no%20brands&width=600&height=400&seq=art-card-electronics-01&orientation=landscape',
    icon: 'ri-smartphone-line',
  },
  {
    slug: 'qc-guide',
    title: 'AllChinaBuy QC Guide',
    desc: 'Learn how to read QC photos, inspect quality factors, and request additional images.',
    image: 'https://readdy.ai/api/search-image?query=Quality%20inspector%20with%20magnifying%20glass%20on%20workbench%20with%20warm%20workshop%20lighting%20professional%20photography%20no%20text%20no%20brands&width=600&height=400&seq=art-card-qc-01&orientation=landscape',
    icon: 'ri-camera-lens-line',
  },
  {
    slug: 'safety-guide',
    title: 'AllChinaBuy Safety Guide',
    desc: 'Payment safety, seller verification, scam prevention, and dispute resolution strategies.',
    image: 'https://readdy.ai/api/search-image?query=Secure%20digital%20lock%20on%20modern%20glass%20surface%20with%20warm%20backlight%20cybersecurity%20concept%20photography%20no%20text%20no%20brands&width=600&height=400&seq=art-card-safety-01&orientation=landscape',
    icon: 'ri-shield-check-line',
  },
  {
    slug: 'beginner-guide',
    title: 'AllChinaBuy Beginner Guide',
    desc: 'Step-by-step walkthrough for first-time buyers from account setup to first delivery.',
    image: 'https://readdy.ai/api/search-image?query=Shopping%20guide%20notebook%20and%20laptop%20on%20cozy%20wooden%20desk%20with%20warm%20natural%20light%20learning%20concept%20photography%20no%20text%20no%20brands&width=600&height=400&seq=art-card-beginner-01&orientation=landscape',
    icon: 'ri-book-open-line',
  },
  {
    slug: 'alternatives',
    title: 'AllChinaBuy Alternatives',
    desc: 'Compare other buying-agent platforms and understand when to explore different options.',
    image: 'https://readdy.ai/api/search-image?query=Multiple%20shopping%20bags%20from%20different%20stores%20on%20marble%20floor%20with%20warm%20lighting%20comparison%20shopping%20photography%20no%20text%20no%20brands&width=600&height=400&seq=art-card-alternatives-01&orientation=landscape',
    icon: 'ri-exchange-line',
  },
  {
    slug: 'shipping-cost',
    title: 'AllChinaBuy Shipping Cost',
    desc: 'Complete breakdown of shipping methods, cost factors, consolidation tips, and customs.',
    image: 'https://readdy.ai/api/search-image?query=Airplane%20cargo%20and%20shipping%20containers%20with%20world%20map%20overlay%20logistics%20concept%20warm%20tones%20photography%20no%20text%20no%20brands&width=600&height=400&seq=art-card-shipping-01&orientation=landscape',
    icon: 'ri-truck-line',
  },
];

export default function PillarArticles() {
  const { t } = useTranslation();
  const lang = useLang();

  return (
    <section className="py-14 md:py-20 bg-background-100">
      <div className="w-full px-4 md:px-6 lg:px-10">
        <div className="text-center mb-10 md:mb-14">
          <h2 className="font-heading text-2xl md:text-3xl font-semibold text-foreground-950 mb-3">
            AllChinaBuy Spreadsheet Guides & Resources
          </h2>
          <p className="text-sm md:text-base text-foreground-600 max-w-2xl mx-auto">
            Explore in-depth guides covering products, shipping, safety, and buying strategies to help you research and shop smarter.
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4 md:gap-5">
          {articles.map((article) => (
            <a
              key={article.slug}
              href={`/${lang}/allchinabuy-${article.slug}/`}
              className="group block bg-background-50 rounded-lg border border-background-200/70 overflow-hidden hover:border-primary-300 transition-all hover:-translate-y-0.5"
              onClick={() => window.scrollTo(0, 0)}
            >
              <div className="aspect-[4/3] overflow-hidden">
                <img
                  src={article.image}
                  alt={article.title}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                />
              </div>
              <div className="p-4 md:p-5">
                <div className="flex items-center gap-2 mb-2">
                  <div className="w-7 h-7 flex items-center justify-center rounded-md bg-primary-100 text-primary-600">
                    <i className={`${article.icon} text-sm`} />
                  </div>
                  <span className="text-xs font-medium text-primary-600 uppercase tracking-wide">Guide</span>
                </div>
                <h3 className="font-heading text-sm md:text-base font-semibold text-foreground-900 mb-2 group-hover:text-primary-700 transition-colors leading-snug">
                  {article.title}
                </h3>
                <p className="text-xs md:text-sm text-foreground-600 leading-relaxed line-clamp-3">
                  {article.desc}
                </p>
                <div className="mt-3 flex items-center gap-1 text-xs font-medium text-primary-600 group-hover:text-primary-700 transition-colors">
                  <span>{t('common.readMore')}</span>
                  <i className="ri-arrow-right-line" />
                </div>
              </div>
            </a>
          ))}
        </div>
      </div>
    </section>
  );
}